<template>
  <div class="relative">
    <button v-if="showCloseButton" class="absolute top-0 right-0" @click="close">
      <CloseIcon color="#4B4C52" class="w-24 h-24" />
    </button>
    <form @submit.prevent="submit">
      <h3 v-if="title" class="font-medium text-m1 mr-24">{{ title }}</h3>
      <p v-if="tips.length === 1" class="text-body text-gray-body mb-16" :class="{ 'mt-16': title }">{{ tips[0] }}</p>
      <CheckmarkList v-else :tips="tips" />
      <slot />
      <TextArea
        id="start-the-chat"
        v-model="message"
        class="mt-16"
        :rows="4"
        prop-classes="!resize-none"
        :max-characters="maxCharacters"
        :presence-required="true"
        :character-counter="false"
        :label-name="labelName"
        :status="helperStatus"
        :helper-text="helperText"
      />
      <Button value="Send" class="w-full mt-16" size="medium" type="submit" :loading="loading" />
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { HelperStatus } from '@/helpers/status';
import Button from '@/components/button/Button.vue';
import CheckmarkList from '@/components/checkmark_list/CheckmarkList.vue';
import TextArea from '@/components/textarea/TextArea.vue';
import CloseIcon from '@/graphics/icons/CloseIcon.vue';

defineOptions({ name: 'SendMessageInput' });

const message = defineModel<string>({ default: '' });

const props = withDefaults(
  defineProps<{
    showCloseButton?: boolean;
    title?: string;
    defaultMessage?: string;
    tips?: string[];
    loading?: boolean;
    labelName: string;
    validationMessage?: string;
    maxCharacters?: number;
  }>(),
  {
    showCloseButton: true,
    title: '',
    defaultMessage: '',
    tips: () => [],
    loading: false,
    validationMessage: 'Please include a message.',
    maxCharacters: 4000,
  }
);

const emit = defineEmits<{
  (e: 'submit', message: string): void;
  (e: 'close'): void;
  (e: 'closeDraft'): void;
}>();

if (props.defaultMessage && !message.value) {
  message.value = props.defaultMessage;
}

const helperStatus = ref(HelperStatus.Neutral);

const trimmedMessage = computed(() => message.value.trim());
const isMessageTooLong = computed(() => trimmedMessage.value.length > props.maxCharacters);
const isValidMessage = computed(() => trimmedMessage.value.length > 0 && !isMessageTooLong.value);
const helperText = computed(() => {
  if (isMessageTooLong.value) {
    return '';
  }
  if (helperStatus.value === HelperStatus.Error) {
    return props.validationMessage;
  }
  return '';
});

watch(message, () => {
  if (isValidMessage.value) {
    helperStatus.value = HelperStatus.Valid;
  }
});

function submit(): void {
  if (isValidMessage.value) {
    emit('submit', trimmedMessage.value);
  } else {
    helperStatus.value = HelperStatus.Error;
  }
}

function close(): void {
  if (isValidMessage.value) {
    emit('closeDraft');
  } else {
    emit('close');
  }
}
</script>
